/* Phase 7 — global reward animator.

Polls /api/student/rewards/pending on mount (and whenever the global
`window.dispatchEvent(new CustomEvent('kks:rewards-refresh'))` fires), then
animates each pending reward in sequence with confetti / stars / applause
/ toast variants. Includes a mute toggle persisted in localStorage. */
import { useCallback, useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fetchPendingRewards, ANIM_MAP, copyFor } from "@/lib/rewards";

let _audioCtx = null;
function playChime() {
  if (localStorage.getItem("kks_audio_muted") === "1") return;
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const now = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(660, now);
    o.frequency.exponentialRampToValueAtTime(990, now + 0.15);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.2, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
    o.start(now);
    o.stop(now + 0.32);
  } catch {
    /* ignore */
  }
}

function playApplause() {
  if (localStorage.getItem("kks_audio_muted") === "1") return;
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const now = ctx.currentTime;
    // 6 quick clap-like noise bursts
    for (let i = 0; i < 6; i++) {
      const bufferSize = ctx.sampleRate * 0.05;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let j = 0; j < bufferSize; j++) {
        data[j] = (Math.random() * 2 - 1) * (1 - j / bufferSize);
      }
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.15, now + i * 0.08);
      src.connect(g);
      g.connect(ctx.destination);
      src.start(now + i * 0.08);
    }
  } catch {
    /* ignore */
  }
}

function fireConfetti(big = false) {
  const count = big ? 200 : 90;
  confetti({
    particleCount: count,
    spread: big ? 110 : 70,
    origin: { y: 0.6 },
    colors: ["#f59e0b", "#fbbf24", "#fde68a", "#84cc16", "#22d3ee", "#a78bfa"],
  });
}

function fireStars(emoji = "⭐", n = 3) {
  // Floating emoji rain at the top-right corner (cheap CSS approach)
  const root = document.body;
  for (let i = 0; i < n; i++) {
    const span = document.createElement("span");
    span.textContent = emoji;
    span.style.cssText = `
      position: fixed;
      right: ${24 + i * 30}px;
      top: 90px;
      font-size: 32px;
      pointer-events: none;
      z-index: 9999;
      transition: transform 1.4s ease-out, opacity 1.4s ease-out;
    `;
    document.body.appendChild(span);
    requestAnimationFrame(() => {
      span.style.transform = `translateY(120px) scale(0.6) rotate(${
        (i - 1) * 30
      }deg)`;
      span.style.opacity = "0";
    });
    setTimeout(() => root.removeChild(span), 1600);
  }
}

async function playReward(reward) {
  const kind = ANIM_MAP[reward.event] || "toast";
  const msg = copyFor(reward.event);
  if (kind === "confetti") {
    fireConfetti(reward.event === "course_completed");
    playChime();
    toast.success(msg);
  } else if (kind === "applause") {
    playApplause();
    fireStars("👏", reward.stars_awarded || 3);
    toast.success(msg);
  } else if (kind === "stars") {
    playChime();
    fireStars("⭐", Math.max(1, reward.stars_awarded || 1));
    toast.success(msg);
  } else {
    toast(msg);
  }
}

export default function RewardAnimator() {
  const { user } = useAuth();
  const playingRef = useRef(false);

  const poll = useCallback(async () => {
    if (!user || user.role !== "student" || playingRef.current) return;
    try {
      const list = await fetchPendingRewards();
      if (!list?.length) return;
      playingRef.current = true;
      for (const r of list) {
        await playReward(r);
        await new Promise((res) => setTimeout(res, 700));
      }
    } catch {
      /* swallow */
    } finally {
      playingRef.current = false;
      // refresh the progress card if mounted
      window.dispatchEvent(new CustomEvent("kks:progress-refresh"));
    }
  }, [user]);

  useEffect(() => {
    poll();
    const onRefresh = () => poll();
    window.addEventListener("kks:rewards-refresh", onRefresh);
    return () => window.removeEventListener("kks:rewards-refresh", onRefresh);
  }, [poll]);

  return null; // pure side-effect component
}
